import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, ShieldCheck, Instagram, Facebook, Linkedin, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { UKGMLogo, UKGM_LINKS } from '@/lib/ui';

export default function PublicFooter() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await api.post('/newsletter', { email });
      toast.success(t('footer.newsletter_success'));
      setEmail('');
    } catch { toast.error(t('contact.form.error')); }
    finally { setSending(false); }
  };

  return (
    <footer className="mt-24 bg-[#0F1E4A] text-white/85" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-4">
              <UKGMLogo className="w-16 h-16" ring={false} />
              <div>
                <div className="font-serif text-white text-xl md:text-2xl leading-tight">Universal Krishna Gaukul Mission</div>
                <div className="mt-1 text-xs tracking-widest uppercase text-ukgm-gold/90">{t('footer.reg')}</div>
              </div>
            </div>
            <p className="mt-5 text-sm max-w-md text-white/70 leading-relaxed">{t('footer.tagline')}</p>

            <div className="mt-6 flex flex-wrap gap-2 text-xs">
              <span className="bg-white/10 border border-ukgm-gold/40 text-ukgm-gold px-3 py-1 rounded-full font-semibold tracking-widest">80G</span>
              <span className="bg-white/10 border border-ukgm-gold/40 text-ukgm-gold px-3 py-1 rounded-full font-semibold tracking-widest">12A</span>
              <span className="bg-white/10 border border-ukgm-gold/40 text-ukgm-gold px-3 py-1 rounded-full font-semibold tracking-widest">CSR-1</span>
              <span className="bg-white/10 border border-ukgm-gold/40 text-ukgm-gold px-3 py-1 rounded-full font-semibold tracking-widest">NGO DARPAN</span>
            </div>
          </div>

          <div>
            <div className="font-serif text-ukgm-gold text-sm tracking-widest uppercase mb-4">{t('footer.quick')}</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-ukgm-gold">{t('nav.about')}</Link></li>
              <li><Link to="/projects" className="hover:text-ukgm-gold">{t('nav.projects')}</Link></li>
              <li><Link to="/transparency" className="hover:text-ukgm-gold">{t('nav.transparency')}</Link></li>
              <li><Link to="/blog" className="hover:text-ukgm-gold">{t('nav.blog')}</Link></li>
              <li><Link to="/media" className="hover:text-ukgm-gold">{t('nav.media')}</Link></li>
              <li><Link to="/contact" className="hover:text-ukgm-gold">{t('nav.contact')}</Link></li>
            </ul>
          </div>

          <div>
            <div className="font-serif text-ukgm-gold text-sm tracking-widest uppercase mb-4">{t('footer.contact')}</div>
            <div className="text-sm space-y-3 text-white/80">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-ukgm-gold shrink-0 mt-0.5" />
                <span className="leading-relaxed text-xs">
                  Room No. 1488, BMC Colony, A.H. Marg, 6 Number, Malvani, Malad (West), Mumbai – 400095, Maharashtra, India
                </span>
              </div>
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-ukgm-gold shrink-0" /> <a href="mailto:ukgm.trust@gmail.com" className="hover:text-ukgm-gold">ukgm.trust@gmail.com</a></div>
              <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-ukgm-gold shrink-0" /> <a href="tel:+917447261155" className="hover:text-ukgm-gold">+91 74472 61155</a></div>
            </div>

            <div className="mt-6 flex items-center gap-2" data-testid="footer-socials">
              <a href={UKGM_LINKS.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-9 h-9 rounded-full border border-ukgm-gold/40 flex items-center justify-center text-ukgm-gold hover:bg-ukgm-gold hover:text-ukgm-blue" data-testid="social-whatsapp"><MessageCircle className="w-4 h-4" /></a>
              <a href={UKGM_LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full border border-ukgm-gold/40 flex items-center justify-center text-ukgm-gold hover:bg-ukgm-gold hover:text-ukgm-blue" data-testid="social-instagram"><Instagram className="w-4 h-4" /></a>
              <a href={UKGM_LINKS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full border border-ukgm-gold/40 flex items-center justify-center text-ukgm-gold hover:bg-ukgm-gold hover:text-ukgm-blue" data-testid="social-facebook"><Facebook className="w-4 h-4" /></a>
              <a href={UKGM_LINKS.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-9 h-9 rounded-full border border-ukgm-gold/40 flex items-center justify-center text-ukgm-gold hover:bg-ukgm-gold hover:text-ukgm-blue" data-testid="social-linkedin"><Linkedin className="w-4 h-4" /></a>
            </div>

            <form onSubmit={subscribe} className="mt-6 flex gap-2" data-testid="newsletter-form">
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                placeholder={t('footer.newsletter_placeholder')}
                className="flex-1 min-w-0 rounded-full px-4 py-2.5 bg-white/10 border border-white/20 text-sm text-white placeholder:text-white/50"
                data-testid="newsletter-email" />
              <button disabled={sending} className="bg-ukgm-gold text-ukgm-blue font-bold px-4 py-2.5 rounded-full text-sm hover:bg-ukgm-goldDark disabled:opacity-50" data-testid="newsletter-submit">
                {t('footer.newsletter_cta')}
              </button>
            </form>
          </div>
        </div>

        <div className="gold-divider my-10" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/60">
          <span>{t('footer.rights', { year: new Date().getFullYear() })}</span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to="/policy/privacy" className="hover:text-ukgm-gold">Privacy Policy</Link>
            <Link to="/policy/terms" className="hover:text-ukgm-gold">Terms & Conditions</Link>
            <Link to="/policy/refund" className="hover:text-ukgm-gold">Refund Policy</Link>
            <Link to="/policy/donation" className="hover:text-ukgm-gold">Donation Policy</Link>
            <Link to="/admin/login" className="hover:text-ukgm-gold" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
